import React, { useEffect, useState } from 'react';
import { Carrier, getCarriers, saveCarrier } from '../services/dbService';

const Carriers: React.FC = () => {
  const [carriers, setCarriers] = useState<Carrier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingCarrier, setEditingCarrier] = useState<Carrier | null>(null);
  
  const [formData, setFormData] = useState<Carrier>({
    name: '',
    email: '',
    contactPerson: '',
    phone: ''
  });
  
  useEffect(() => {
    const fetchCarriers = async () => {
      try {
        const fetchedCarriers = await getCarriers();
        setCarriers(fetchedCarriers);
      } catch (err: any) {
        console.error('Error fetching carriers:', err);
        setError(err.message || 'Failed to load carriers');
      } finally {
        setLoading(false);
      }
    };
    
    fetchCarriers();
  }, []);
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      contactPerson: '',
      phone: ''
    });
    setEditingCarrier(null);
    setFormOpen(false);
  };
  
  const handleEdit = (carrier: Carrier) => {
    setEditingCarrier(carrier);
    setFormData({ ...carrier });
    setFormOpen(true);
  };
  
  const handleDelete = async (carrierId: string) => {
    if (!window.confirm('Are you sure you want to delete this carrier?')) {
      return;
    }
    
    try {
      // In a real app, you would call a delete function
      // For now, we'll just update the UI
      setCarriers(carriers.filter(c => c.id !== carrierId));
    } catch (err: any) {
      console.error('Error deleting carrier:', err);
      alert(`Failed to delete carrier: ${err.message}`);
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (!formData.name.trim()) {
        alert('Carrier name is required');
        return;
      }
      
      const carrierToSave: Carrier = {
        ...formData,
        id: editingCarrier?.id
      };
      
      const carrierId = await saveCarrier(carrierToSave);
      
      if (editingCarrier) {
        // Update existing carrier
        setCarriers(carriers.map(c => 
          c.id === editingCarrier.id ? { ...carrierToSave, id: carrierId } : c
        ));
      } else {
        // Add new carrier
        setCarriers([...carriers, { ...carrierToSave, id: carrierId }]);
      }
      
      resetForm();
    } catch (err: any) {
      console.error('Error saving carrier:', err);
      alert(`Failed to save carrier: ${err.message}`);
    }
  };
  
  if (loading) {
    return <div className="loading">Loading carriers...</div>;
  }
  
  if (error) {
    return <div className="error-message">{error}</div>;
  }
  
  return (
    <div className="carriers-page">
      <div className="page-header">
        <h1>NFI Lumper Receipt Creator | Carriers</h1>
        {!formOpen && (
          <button 
            className="add-btn" 
            onClick={() => setFormOpen(true)}
          >
            Add Carrier
          </button>
        )}
      </div>
      
      {formOpen && (
        <div className="form-container">
          <h2>{editingCarrier ? 'Edit Carrier' : 'Add Carrier'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="name">Carrier Name*</label>
              <input 
                type="text" 
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input 
                type="email" 
                id="email"
                name="email"
                value={formData.email || ''}
                onChange={handleInputChange}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="contactPerson">Contact Person</label>
              <input 
                type="text" 
                id="contactPerson"
                name="contactPerson"
                value={formData.contactPerson || ''}
                onChange={handleInputChange}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="phone">Phone</label>
              <input 
                type="tel" 
                id="phone"
                name="phone"
                value={formData.phone || ''}
                onChange={handleInputChange}
              />
            </div>
            
            <div className="form-actions">
              <button type="submit" className="save-btn">
                {editingCarrier ? 'Update' : 'Save'}
              </button>
              <button 
                type="button" 
                className="cancel-btn" 
                onClick={resetForm}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
      
      {carriers.length === 0 ? (
        <p className="no-data">No carriers found. Add a carrier to get started.</p>
      ) : (
        <div className="carriers-list">
          {carriers.map(carrier => (
            <div key={carrier.id} className="carrier-card">
              <div className="carrier-info">
                <h3>{carrier.name}</h3>
                {carrier.email && <p><strong>Email:</strong> {carrier.email}</p>}
                {carrier.contactPerson && <p><strong>Contact:</strong> {carrier.contactPerson}</p>}
                {carrier.phone && <p><strong>Phone:</strong> {carrier.phone}</p>}
              </div>
              <div className="carrier-actions">
                <button 
                  className="edit-btn" 
                  onClick={() => handleEdit(carrier)}
                >
                  Edit
                </button>
                <button 
                  className="delete-btn" 
                  onClick={() => handleDelete(carrier.id!)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Carriers; 